/**
 * Wallet: balance, top-up, payment method, transactions.
 *
 * The balance and the card both come from GET /api/billing/wallet, which
 * derives `hasPaymentMethod` from the WHOP vaulted card. That matters: the
 * sidebar pill reads GET /api/stripe/wallet, whose `hasPaymentMethod` is the
 * STRIPE payment method - a different rail. Two endpoints, similar names,
 * different meanings. This screen must stay on the billing one, or the hero and
 * the payment-method row below it can contradict each other.
 *
 * We never see a card number - the card is vaulted at Whop - so there is no
 * "•••• 4242" to render, and inventing one would be a lie about data we do not
 * hold.
 *
 * @module web/screens/app/WalletScreen
 */
import { useEffect, useState } from "react";
import { get, raw } from "../../api/client.ts";
import type { PricingDisplay, TopupOptions, WalletEvent, WalletResponse } from "../../api/types.ts";
import { fmtEur, fmtEurCompact } from "../../money.ts";
import { useBrand } from "../../state/BrandContext.tsx";
import { resolveVaultReturn, startVault } from "../../state/vault.ts";
import { mountWhopLoader, whopEmbedAttributes } from "./whopEmbed.ts";
import { PaySheet, type PaySheetSpec } from "../../components/PaySheet.tsx";
import { CardIcon, WalletIcon } from "./payIcons.tsx";

const TX_LABELS: Record<string, string> = {
  topup: "Top-up", brief_charge: "Brief", deduction: "Brief",
  charge: "Charge", refund: "Refund", adjustment: "Adjustment",
};

const TIER_NAMES: Record<string, string> = {
  none: "PAYG", tier1: "Prepaid Tier 1", tier2: "Prepaid Tier 2", tier3: "Prepaid Tier 3",
};

/**
 * One idempotency token per sheet opening.
 *
 * It is what makes a double tap, a retried fetch or a flaky connection settle
 * onto ONE charge: the server uses it as the Whop Idempotency-Key and as the
 * ledger ref. A brief confirm does not need one - it is idempotent on the brief
 * id - but a top-up has no natural key, so this IS the key.
 */
function newIntentId(): string {
  const uuid = globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `i-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  return uuid.replace(/[^A-Za-z0-9._-]/g, "");
}

export function WalletScreen() {
  const { brand, refresh, notify } = useBrand();
  const [wallet, setWallet] = useState<WalletResponse | null>(null);
  const [pricing, setPricing] = useState<PricingDisplay | null>(null);
  const [options, setOptions] = useState<TopupOptions | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [sheet, setSheet] = useState<PaySheetSpec | null>(null);

  useEffect(() => { void load(); }, []);

  async function load(): Promise<void> {
    try {
      setWallet(await get<WalletResponse>("/api/billing/wallet"));
    } catch {
      // The screen still renders its shell; a balance we cannot read is not a
      // reason to show a wrong one.
    }
    try {
      setPricing(await get<PricingDisplay>("/api/pricing/display"));
    } catch {
      // Leave the neutral placeholder rather than guess a rate.
    }
  }

  async function openTopup(): Promise<void> {
    setChoosing(true);
    if (options) return;
    try {
      setOptions(await get<TopupOptions>("/api/billing/topup/options"));
    } catch {
      notify("Could not load top-up amounts.", "error");
    }
  }

  function startTopup(cents: number, bonusCents: number): void {
    const hasCard = options?.hasCardOnFile ?? brand?.hasCardOnFile ?? false;
    const intentId = newIntentId();
    setSheet({
      eyebrow: "Top up balance",
      title: bonusCents > 0
        ? `${fmtEurCompact(cents)} + ${fmtEurCompact(bonusCents)} free`
        : `Top up ${fmtEurCompact(cents)}`,
      amountCents: cents,
      icon: hasCard ? <CardIcon /> : <WalletIcon />,
      label: hasCard ? "Card on file" : "No card on file",
      cta: `Pay ${fmtEurCompact(cents)}`,
      note: hasCard
        ? "Charged now. Credit lands immediately."
        : "No card on file yet - add one to top up.",
      ...(hasCard ? {} : { blocked: true }),
      settle: () => raw("/api/billing/topup", {
        method: "POST",
        body: JSON.stringify({ amountCents: cents, intentId }),
      }),
      done: () => ({ h: "Topped up", s: "Your balance is ready." }),
      after: () => { setChoosing(false); void load(); void refresh(); },
    });
  }

  const balance = wallet?.balanceCents ?? 0;
  const hasCard = wallet?.hasPaymentMethod ?? false;

  return (
    <>
      <header className="page-head fade-in">
        <h1 className="t-h2">Wallet</h1>
        <p className="t-body" style={{ marginTop: 4 }}>Your token balance for on-demand briefs.</p>
      </header>

      <div className="card fade-in" style={{ marginTop: 28, maxWidth: 560 }}>
        <div className="t-eyebrow">Current balance</div>
        <div className="t-display" style={{ margin: "10px 0 4px", fontVariantNumeric: "tabular-nums" }} id="wallet-balance">
          {fmtEurCompact(balance)}
        </div>
        <div className="t-mono">
          {pricing
            ? `${TIER_NAMES[pricing.effectiveTier] ?? "PAYG"} · ${fmtEur(pricing.tokenRateCents)}/token`
            : "-"}
        </div>
        <div className="t-body-sm" style={{ marginTop: 6 }} id="wallet-card-status">
          {hasCard ? "Card on file" : "No payment method saved"}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
          <button className="btn btn-brand" onClick={() => void openTopup()}>Top up</button>
          <AddCard hasCard={hasCard} onResolved={() => { void load(); void refresh(); }} />
        </div>
      </div>

      {choosing && (
        <div style={{ marginTop: 28 }} className="fade-in">
          <div className="rule" style={{ margin: "0 0 18px" }}>Choose amount</div>
          <div className="wallet-topup-grid">
            {(options?.presets ?? []).map(p => (
              <button
                className={`topup-option${p.recommended ? " recommended" : ""}`}
                key={p.cents}
                data-cents={p.cents}
                onClick={() => startTopup(p.cents, p.bonusCents)}
              >
                {p.recommended && <span className="topup-badge">Recommended</span>}
                <div className="topup-amount">{fmtEurCompact(p.cents)}</div>
                {p.bonusCents > 0
                  ? <div className="topup-bonus">+ {fmtEurCompact(p.bonusCents)} free credit</div>
                  : <div className="topup-briefs">Balance for your briefs</div>}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={() => setChoosing(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="rule" style={{ margin: "40px 0 18px" }}>Payment method</div>
      <div className="integrations-card glass fade-in">
        <div className="integrations-list">
          <div className="integration-row">
            <div>
              <div className="t-body">Payment method</div>
              <div className="t-body-sm">
                {hasCard
                  ? "Your card is on file - top-ups and briefings are one tap."
                  : "Add a card once; every order after that is a single tap."}
              </div>
            </div>
            <span className={`pill ${hasCard ? "pill-success" : "pill-warn"}`}>{hasCard ? "On file" : "Not saved"}</span>
          </div>
        </div>
      </div>

      <div className="rule" style={{ margin: "40px 0 18px" }}>Recent transactions</div>
      <div className="tx-list">
        {(wallet?.events ?? []).length === 0
          ? <div className="t-body-sm" style={{ padding: "14px 0", color: "var(--ink-3)" }}>No transactions yet.</div>
          : wallet!.events.map(e => <TxRow key={e.id} event={e} />)}
      </div>

      <PaySheet spec={sheet} onClose={() => setSheet(null)} />
    </>
  );
}

function TxRow({ event }: { event: WalletEvent }) {
  const credit = event.amountCents > 0;
  // A gift is its own line and says so - it is not money the customer paid.
  const kind = event.bonus ? "Bonus credit" : TX_LABELS[event.kind] ?? event.kind;
  const when = new Date(event.createdAt).toLocaleString("en-IE", { dateStyle: "medium", timeStyle: "short" });
  return (
    <div className="tx-row">
      <div>
        <div className="tx-desc">{event.description || kind}</div>
        <div className="tx-meta">{kind} · {when}</div>
      </div>
      <div className={`tx-amount${credit ? "" : " negative"}`}>
        {credit ? "+" : "-"} {fmtEur(Math.abs(event.amountCents))}
      </div>
    </div>
  );
}

/**
 * Putting a card on file.
 *
 * The card is vaulted at Whop and we never see it. Until 2026-08-23 that meant
 * a hand-off to their hosted checkout in a second tab, and a button to press on
 * the way back. Whop then withdrew setup-mode sessions from the hosted page
 * (`setup_mode_unsupported`) while their EMBEDDED checkout kept rendering the
 * same configuration as a working card form - see screens/app/whopEmbed.ts.
 *
 * So the form is mounted here, in place. That is also the flow CLAUDE.md asks
 * for and the redirect never was: no second tab, no leaving the portal.
 *
 * The "I've added my card" button stays. The embed can finish by redirecting to
 * `?vault=return` (handled in state/vault.ts) but it does not have to, and a
 * customer who has typed their card in must never be left with nothing to press.
 */
function AddCard({ hasCard, onResolved }: { hasCard: boolean; onResolved: () => void }) {
  const { notify } = useBrand();
  const [session, setSession] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // After the div exists, not during render: the loader mounts whatever is in
  // the DOM when it runs.
  useEffect(() => {
    if (session) mountWhopLoader();
  }, [session]);

  async function open(): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      const setup = await startVault();
      if (setup.hasCardOnFile) { onResolved(); return; }
      if (!setup.configured || !setup.sessionId) {
        // A setup gap on our side, not something the customer did.
        notify("Card payments are not switched on for this account yet. Ping us and we'll enable it.", "error");
        return;
      }
      setSession(setup.sessionId);
    } catch {
      notify("Could not open the card form. Try again in a moment.", "error");
    } finally {
      setBusy(false);
    }
  }

  async function resolve(): Promise<void> {
    if (busy) return;
    setBusy(true);
    // One lookup per tap, never a loop: a poll that never stops would hammer
    // Whop for a card that may simply not exist yet.
    const outcome = await resolveVaultReturn();
    setBusy(false);
    notify(outcome.message, outcome.ok ? "success" : "error");
    if (outcome.ok) { setSession(null); onResolved(); }
  }

  const returnUrl = `${window.location.origin}/?vault=return`;

  return (
    <>
      {!session && (
        <button className={`btn${hasCard ? "" : " btn-brand"}`} id="wallet-add-card" onClick={() => void open()} disabled={busy}>
          {hasCard ? "Replace card" : "Add card"}
        </button>
      )}
      {session && (
        <div className="whop-card-form" id="wallet-card-form">
          <div id="whop-embed-target" {...whopEmbedAttributes(session, returnUrl)} />
          <div className="whop-card-actions">
            <button className="btn" id="wallet-resolve-card" onClick={() => void resolve()} disabled={busy}>
              {busy ? "Checking…" : "I've added my card"}
            </button>
            <button className="btn" id="wallet-cancel-card" onClick={() => setSession(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
